// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Radar, Activity, Lock, BarChart3 } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen radar-grid-bg flex">
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="relative w-9 h-9 flex items-center justify-center rounded-sm border border-[#262626] bg-[#141414]">
              <Radar className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.6}/>
              <span className="absolute inset-0 rounded-sm ring-1 ring-[#0A84FF]/20 animate-pulse"/>
            </div>
            <div>
              <div className="font-display font-semibold text-lg tracking-tight text-white">RADAR</div>
              <div className="kpi-label">Account Intelligence</div>
            </div>
          </div>

          <div className="kpi-label mb-3">/ SIGN IN · radaracctintel.com</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-white leading-[1.05]">
            Your account intelligence, <span className="text-[#0A84FF]">personalised.</span>
          </h1>
          <p className="text-[#A3A3A3] mt-4 text-[15px] leading-relaxed">
            Sign in with your Google account to access RADAR. Every report, activity and deal you log
            is tagged to your identity — so managers can review consolidated productivity across the team.
          </p>

          <button
            type="button"
            onClick={login}
            data-testid="google-signin-btn"
            className="mt-8 w-full h-12 rounded-sm bg-white hover:bg-[#F5F5F5] text-[#0A0A0A] font-medium flex items-center justify-center gap-3 transition-colors group"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            <span className="text-sm">Continue with Google</span>
          </button>

          <div className="mt-6 text-[11px] text-[#525252] font-mono-data">
            By continuing, you accept tracking of your activity for team productivity reporting.
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 text-[11px] text-[#737373]">
            <div className="border border-[#262626] rounded-sm p-3 flex flex-col gap-1.5">
              <Lock className="w-3.5 h-3.5 text-[#0A84FF]" strokeWidth={1.6}/>
              <span>Secure · httpOnly cookie</span>
            </div>
            <div className="border border-[#262626] rounded-sm p-3 flex flex-col gap-1.5">
              <Activity className="w-3.5 h-3.5 text-[#32D74B]" strokeWidth={1.6}/>
              <span>Activity auto-tagged</span>
            </div>
            <div className="border border-[#262626] rounded-sm p-3 flex flex-col gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-[#FF9F0A]" strokeWidth={1.6}/>
              <span>Team-level reporting</span>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 relative border-l border-[#262626] items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "url('https://images.unsplash.com/photo-1774646598677-cc38cb3cac00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRvcG9ncmFwaHklMjBkYXJrJTIwbWFwfGVufDB8fHx8MTc3NzY5MDE3MHww&ixlib=rb-4.1.0&q=85')",
                      backgroundSize: "cover", backgroundPosition: "center" }}/>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/30 via-transparent to-[#0A0A0A]/80"/>
        <div className="relative max-w-sm text-center">
          <div className="radar-sweep mx-auto mb-6"/>
          <div className="kpi-label text-[#0A84FF] mb-2">/ RADAR ENGINE · LIVE</div>
          <p className="text-sm text-[#A3A3A3] leading-relaxed">
            21-section executive intelligence · 6-section BDR pipeline analytics · Real-time productivity scoring.
          </p>
        </div>
      </div>
    </div>
  );
}
